import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Users, Church, BookOpen, RefreshCw, Download, Search, Loader2,
  Eye, Key, Trash2, Edit2, Crown, TrendingUp, DollarSign, GraduationCap,
  Baby, Sprout, Shield, ChevronRight, X, User, Mail, Calendar,
  ArrowUpCircle, UserPlus, Check, Music
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import WorshipSongsAdmin from '@/components/christian/WorshipSongsAdmin';

const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];

const ChristianAdminSection = ({ 
  christianStats, 
  christianUsers, 
  christianChildren, 
  christianLoading, 
  fetchChristianStats,
  api 
}) => {
  // State for modals and drill-downs
  const [activeModal, setActiveModal] = useState(null); // 'users', 'children', 'user-detail'
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'songs'
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  
  // User management states
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [deleteUserDialog, setDeleteUserDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Upgrade dialog state
  const [upgradeDialog, setUpgradeDialog] = useState({ open: false, user: null });
  const [upgradePlan, setUpgradePlan] = useState('christian');
  const [upgrading, setUpgrading] = useState(false);
  
  // Add user to Christian module state
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [searchPlatformUsers, setSearchPlatformUsers] = useState('');
  const [loadingPlatformUsers, setLoadingPlatformUsers] = useState(false);
  
  // Detailed users list for drill-down
  const [detailedUsers, setDetailedUsers] = useState([]);
  const [detailedChildren, setDetailedChildren] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Fetch detailed user list
  const fetchDetailedUsers = async (filter = {}) => {
    setLoadingDetail(true);
    try {
      let url = '/admin/christian/users?limit=100';
      if (filter.search) url += `&search=${filter.search}`;
      const response = await api.get(url);
      let users = response.data.users || [];
      
      // Filter by plan if needed
      if (filter.plan && filter.plan !== 'all') {
        users = users.filter(u => u.subscription_plan === filter.plan);
      }
      
      setDetailedUsers(users);
    } catch (error) {
      console.error('Failed to fetch detailed users:', error);
      toast.error('Failed to load user details');
    } finally {
      setLoadingDetail(false);
    }
  };
  
  // Fetch detailed children list
  const fetchDetailedChildren = async (filter = {}) => {
    setLoadingDetail(true);
    try {
      let url = '/admin/christian/children?limit=100';
      if (filter.level && filter.level !== 'all') {
        url += `&level=${filter.level}`;
      }
      const response = await api.get(url);
      setDetailedChildren(response.data.children || []);
    } catch (error) {
      console.error('Failed to fetch detailed children:', error);
      toast.error('Failed to load children details');
    } finally {
      setLoadingDetail(false);
    }
  };
  
  // Fetch single user detail
  const fetchUserDetail = async (userId) => {
    setLoadingDetail(true);
    try {
      const response = await api.get(`/admin/christian/users/${userId}`);
      setSelectedUser(response.data);
      setActiveModal('user-detail');
    } catch (error) {
      console.error('Failed to fetch user detail:', error);
      toast.error('Failed to load user details');
    } finally {
      setLoadingDetail(false);
    }
  };
  
  // Handle user update
  const handleUpdateUser = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await api.put(`/admin/christian/users/${editingUser.id}`, {
        name: editingUser.name,
        subscription_plan: editingUser.subscription_plan,
        christian_education_enabled: editingUser.christian_education_enabled,
        academic_access: editingUser.academic_access
      });
      toast.success('User updated successfully');
      setEditUserDialog(false);
      setEditingUser(null);
      fetchChristianStats();
      if (activeModal === 'users') fetchDetailedUsers({ search: searchQuery, plan: planFilter });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle password reset
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/christian/users/${editingUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setResetPasswordDialog(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle user deletion
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setSaving(true);
    try {
      await api.delete(`/admin/christian/users/${userToDelete.id}`);
      toast.success('User deleted successfully');
      setDeleteUserDialog(false);
      setUserToDelete(null);
      fetchChristianStats();
      if (activeModal === 'users') fetchDetailedUsers({ search: searchQuery, plan: planFilter });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  // Handle plan upgrade
  const handleUpgradeUser = async () => {
    if (!upgradeDialog.user) return;
    setUpgrading(true);
    try {
      await api.put(`/admin/christian/users/${upgradeDialog.user.id}/upgrade?plan=${upgradePlan}`);
      toast.success(`User upgraded to ${upgradePlan === 'christian_plus' ? 'Christian Plus' : 'Christian'} plan!`);
      setUpgradeDialog({ open: false, user: null });
      fetchChristianStats();
      if (activeModal === 'users') fetchDetailedUsers({ search: searchQuery, plan: planFilter });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upgrade user');
    } finally {
      setUpgrading(false);
    }
  };

  // Fetch platform users without Christian Education
  const fetchPlatformUsers = async (search = '') => {
    setLoadingPlatformUsers(true);
    try {
      const response = await api.get(`/admin/users?limit=50&search=${search}`);
      // Filter users who don't have Christian Education enabled
      const users = (response.data.users || []).filter(u => !u.christian_education_enabled);
      setPlatformUsers(users);
    } catch (error) {
      console.error('Failed to fetch platform users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingPlatformUsers(false);
    }
  };

  // Enable Christian Education for a platform user
  const handleEnableChristianForUser = async (userId, userName) => {
    try {
      await api.put(`/admin/users/${userId}`, { christian_education_enabled: true });
      toast.success(`Christian Education enabled for ${userName}`);
      fetchPlatformUsers(searchPlatformUsers);
      fetchChristianStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable Christian Education');
    }
  };
  
  // Handle export
  const handleExportUsers = async () => {
    setExporting(true);
    try {
      const response = await api.get('/admin/christian/users?limit=1000');
      const users = response.data.users || [];
      
      // Create CSV
      const headers = ['Name', 'Email', 'Plan', 'Children', 'Created At'];
      const rows = users.map(u => [
        u.name,
        u.email,
        u.subscription_plan,
        u.children_count || 0,
        new Date(u.created_at).toLocaleDateString()
      ]);
      
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `christian_users_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      
      toast.success('Export completed');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };
  
  // Handle single user export
  const handleExportUserData = async (userId) => {
    setExporting(true);
    try {
      const response = await api.get(`/admin/christian/users/${userId}/export`);
      const data = response.data;
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user_${userId}_export.json`;
      a.click();
      
      toast.success('User data exported');
    } catch (error) {
      toast.error('Failed to export user data');
    } finally {
      setExporting(false);
    }
  };
  
  // Stats card click handlers
  const handleStatClick = (type) => {
    switch (type) {
      case 'total_users':
        setActiveModal('users');
        setPlanFilter('all');
        fetchDetailedUsers({});
        break;
      case 'free_users':
        setActiveModal('users');
        setPlanFilter('christian_free');
        fetchDetailedUsers({ plan: 'christian_free' });
        break;
      case 'paid_users':
        setActiveModal('users');
        setPlanFilter('christian');
        fetchDetailedUsers({ plan: 'christian' });
        break;
      case 'plus_users':
        setActiveModal('users');
        setPlanFilter('christian_plus');
        fetchDetailedUsers({ plan: 'christian_plus' });
        break;
      case 'total_children':
        setActiveModal('children');
        setLevelFilter('all');
        fetchDetailedChildren({});
        break;
      case 'seeds':
        setActiveModal('children');
        setLevelFilter('seeds');
        fetchDetailedChildren({ level: 'seeds' });
        break;
      case 'roots':
        setActiveModal('children');
        setLevelFilter('roots');
        fetchDetailedChildren({ level: 'roots' });
        break;
      case 'warriors':
        setActiveModal('children');
        setLevelFilter('warriors');
        fetchDetailedChildren({ level: 'warriors' });
        break;
      default:
        break;
    }
  };
  
  // Level data for pie chart
  const levelData = christianStats?.children?.by_level ? [
    { name: 'Seeds (3-5)', value: christianStats.children.by_level.seeds || 0 },
    { name: 'Roots (6-9)', value: christianStats.children.by_level.roots || 0 },
    { name: 'Warriors (10-13)', value: christianStats.children.by_level.warriors || 0 }
  ] : [];
  
  // Plan data for pie chart
  const planData = christianStats?.subscribers ? [
    { name: 'Free', value: christianStats.subscribers.christian_free || 0 },
    { name: 'Basic ($2.99)', value: christianStats.subscribers.christian || 0 },
    { name: 'Plus ($4.99)', value: christianStats.subscribers.christian_plus || 0 }
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Church className="w-7 h-7 text-indigo-600" />
            Christian Education Admin
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage Christian Education subscribers and content</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportUsers} disabled={exporting} data-testid="export-christian-users">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export Users
          </Button>
          <Button variant="outline" onClick={fetchChristianStats} disabled={christianLoading}>
            {christianLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        <button onClick={() => setActiveTab('overview')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            activeTab === 'overview' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`} data-testid="christian-tab-overview">
          <Church className="w-4 h-4" /> Overview & Users
        </button>
        <button onClick={() => setActiveTab('songs')}
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            activeTab === 'songs' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`} data-testid="christian-tab-songs">
          <Music className="w-4 h-4" /> Worship Songs
        </button>
      </div>

      {/* Songs Tab */}
      {activeTab === 'songs' && (
        <WorshipSongsAdmin api={api} />
      )}
      
      {/* Overview Tab */}
      {activeTab === 'overview' && (christianLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      ) : (
        <>
          {/* Stats Grid - Clickable Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Users */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-indigo-300" 
              onClick={() => handleStatClick('total_users')}
              data-testid="christian-total-users-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Subscribers</p>
                    <p className="text-2xl font-bold text-slate-900">{christianStats?.subscribers?.total || 0}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center text-xs text-indigo-600">
                  <ChevronRight className="w-4 h-4" /> Click to view all users
                </div>
              </CardContent>
            </Card>
            
            {/* Free Users */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-slate-300" 
              onClick={() => handleStatClick('free_users')}
              data-testid="christian-free-users-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <User className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Free Tier</p>
                    <p className="text-2xl font-bold text-slate-900">{christianStats?.subscribers?.christian_free || 0}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center text-xs text-slate-500">
                  <ChevronRight className="w-4 h-4" /> 2-week access
                </div>
              </CardContent>
            </Card>
            
            {/* Paid Users */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-blue-300" 
              onClick={() => handleStatClick('paid_users')}
              data-testid="christian-paid-users-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Crown className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Basic Plan</p>
                    <p className="text-2xl font-bold text-slate-900">{christianStats?.subscribers?.christian || 0}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center text-xs text-blue-600">
                  <ChevronRight className="w-4 h-4" /> $2.99/month
                </div>
              </CardContent>
            </Card>
            
            {/* Plus Users */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-purple-300" 
              onClick={() => handleStatClick('plus_users')}
              data-testid="christian-plus-users-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Crown className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Plus Plan</p>
                    <p className="text-2xl font-bold text-slate-900">{christianStats?.subscribers?.christian_plus || 0}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center text-xs text-purple-600">
                  <ChevronRight className="w-4 h-4" /> $4.99/month
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Second Row - Children & Revenue */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Children */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-emerald-300" 
              onClick={() => handleStatClick('total_children')}
              data-testid="christian-total-children-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Children</p>
                    <p className="text-2xl font-bold text-slate-900">{christianStats?.children?.total || 0}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center text-xs text-emerald-600">
                  <ChevronRight className="w-4 h-4" /> Click to view all
                </div>
              </CardContent>
            </Card>
            
            {/* Seeds */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-amber-300" 
              onClick={() => handleStatClick('seeds')}
              data-testid="christian-seeds-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Baby className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Seeds (3-5)</p>
                    <p className="text-2xl font-bold text-slate-900">{christianStats?.children?.by_level?.seeds || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Roots */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-green-300" 
              onClick={() => handleStatClick('roots')}
              data-testid="christian-roots-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Sprout className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Roots (6-9)</p>
                    <p className="text-2xl font-bold text-slate-900">{christianStats?.children?.by_level?.roots || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Warriors */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-red-300" 
              onClick={() => handleStatClick('warriors')}
              data-testid="christian-warriors-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Warriors (10-13)</p>
                    <p className="text-2xl font-bold text-slate-900">{christianStats?.children?.by_level?.warriors || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Engagement & Revenue Row */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Engagement Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                  Engagement Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Verses Memorized</span>
                  <span className="font-bold text-lg">{christianStats?.engagement?.verses_memorized || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Lessons Completed</span>
                  <span className="font-bold text-lg">{christianStats?.engagement?.lessons_completed || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                  <span className="text-emerald-700 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    Graduation Ready
                  </span>
                  <span className="font-bold text-lg text-emerald-700">{christianStats?.engagement?.graduation_ready || 0}</span>
                </div>
              </CardContent>
            </Card>
            
            {/* Revenue */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  Revenue
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">Monthly Recurring Revenue</p>
                  <p className="text-4xl font-bold text-emerald-600">${christianStats?.revenue?.mrr || '0.00'}</p>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">New This Week</span>
                  <Badge variant="outline" className="text-indigo-600">
                    +{christianStats?.revenue?.recent_signups || 0} signups
                  </Badge>
                </div>
              </CardContent>
            </Card>
            
            {/* Plan Distribution Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Plan Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={planData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                      >
                        {planData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Recent Users Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent Subscribers</CardTitle>
                <CardDescription>Latest Christian Education registrations</CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleStatClick('total_users')}
                data-testid="view-all-christian-users"
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {christianUsers.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No Christian Education subscribers yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Children</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {christianUsers.slice(0, 10).map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="text-slate-500">{user.email}</TableCell>
                        <TableCell>
                          <Badge variant={
                            user.subscription_plan === 'christian_plus' ? 'default' :
                            user.subscription_plan === 'christian' ? 'secondary' : 'outline'
                          }>
                            {user.subscription_plan === 'christian_plus' ? 'Plus' :
                             user.subscription_plan === 'christian' ? 'Basic' : 'Free'}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.children_count || 0}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => fetchUserDetail(user.id)}
                              data-testid={`view-user-${user.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setEditingUser({ ...user });
                                setEditUserDialog(true);
                              }}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      ))}
      
      {/* Users List Modal */}
      <Dialog open={activeModal === 'users'} onOpenChange={(open) => !open && setActiveModal(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Christian Education Users
            </DialogTitle>
            <DialogDescription>
              {planFilter === 'all' ? 'All subscribers' : 
               planFilter === 'christian_free' ? 'Free tier users' :
               planFilter === 'christian' ? 'Basic plan subscribers' : 'Plus plan subscribers'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Filters */}
          <div className="flex gap-4 py-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchDetailedUsers({ search: searchQuery, plan: planFilter })}
                className="pl-10"
                data-testid="christian-user-search"
              />
            </div>
            <Select value={planFilter} onValueChange={(v) => {
              setPlanFilter(v);
              fetchDetailedUsers({ search: searchQuery, plan: v });
            }}>
              <SelectTrigger className="w-40" data-testid="christian-plan-filter">
                <SelectValue placeholder="Filter by plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="christian_free">Free</SelectItem>
                <SelectItem value="christian">Basic ($2.99)</SelectItem>
                <SelectItem value="christian_plus">Plus ($4.99)</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleExportUsers} disabled={exporting}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button 
              onClick={() => {
                setAddUserDialog(true);
                fetchPlatformUsers('');
              }}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Add Users
            </Button>
          </div>
          
          {/* Users Table */}
          <div className="flex-1 overflow-auto">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Children</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell className="text-slate-500">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={
                          user.subscription_plan === 'christian_plus' ? 'default' :
                          user.subscription_plan === 'christian' ? 'secondary' : 'outline'
                        }>
                          {user.subscription_plan === 'christian_plus' ? 'Plus' :
                           user.subscription_plan === 'christian' ? 'Basic' : 'Free'}
                        </Badge>
                      </TableCell>
                      <TableCell>{user.children_count || 0}</TableCell>
                      <TableCell className="text-slate-500">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => fetchUserDetail(user.id)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-indigo-600 hover:text-indigo-800"
                            onClick={() => {
                              setUpgradeDialog({ open: true, user });
                              setUpgradePlan(user.subscription_plan === 'christian' ? 'christian_plus' : 'christian');
                            }}
                            title="Upgrade Plan"
                            data-testid={`upgrade-user-${user.id}`}
                          >
                            <ArrowUpCircle className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setEditingUser({ ...user });
                              setEditUserDialog(true);
                            }}
                            title="Edit User"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleExportUserData(user.id)}
                            title="Export Data"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => {
                              setUserToDelete(user);
                              setDeleteUserDialog(true);
                            }}
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {detailedUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-slate-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Children List Modal */}
      <Dialog open={activeModal === 'children'} onOpenChange={(open) => !open && setActiveModal(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600" />
              Christian Education Children
            </DialogTitle>
            <DialogDescription>
              {levelFilter === 'all' ? 'All enrolled children' : 
               `${levelFilter.charAt(0).toUpperCase() + levelFilter.slice(1)} level children`}
            </DialogDescription>
          </DialogHeader>
          
          {/* Filters */}
          <div className="flex gap-4 py-3">
            <Select value={levelFilter} onValueChange={(v) => {
              setLevelFilter(v);
              fetchDetailedChildren({ level: v });
            }}>
              <SelectTrigger className="w-40" data-testid="christian-level-filter">
                <SelectValue placeholder="Filter by level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="seeds">Seeds (3-5)</SelectItem>
                <SelectItem value="roots">Roots (6-9)</SelectItem>
                <SelectItem value="warriors">Warriors (10-13)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Children Table */}
          <div className="flex-1 overflow-auto">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead>Parent</TableHead>
                    <TableHead>Scriptures</TableHead>
                    <TableHead>Lessons</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailedChildren.map((child) => (
                    <TableRow key={child.id}>
                      <TableCell className="font-medium">{child.name}</TableCell>
                      <TableCell>
                        <Badge variant={
                          child.level === 'warriors' ? 'destructive' :
                          child.level === 'roots' ? 'default' : 'secondary'
                        }>
                          {child.level === 'seeds' ? '🌱 Seeds' :
                           child.level === 'roots' ? '🌿 Roots' : '⚔️ Warriors'}
                        </Badge>
                      </TableCell>
                      <TableCell>{child.age || 'N/A'}</TableCell>
                      <TableCell className="text-slate-500">
                        {child.parent?.name || 'Unknown'}
                      </TableCell>
                      <TableCell>{child.scriptures_memorized || 0}</TableCell>
                      <TableCell>{child.lessons_completed || 0}</TableCell>
                    </TableRow>
                  ))}
                  {detailedChildren.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-slate-500">
                        No children found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* User Detail Modal */}
      <Dialog open={activeModal === 'user-detail'} onOpenChange={(open) => !open && setActiveModal(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              User Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
          ) : selectedUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Name</p>
                  <p className="font-medium">{selectedUser.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Plan</p>
                  <Badge variant={
                    selectedUser.subscription_plan === 'christian_plus' ? 'default' :
                    selectedUser.subscription_plan === 'christian' ? 'secondary' : 'outline'
                  }>
                    {selectedUser.subscription_plan === 'christian_plus' ? 'Plus ($4.99)' :
                     selectedUser.subscription_plan === 'christian' ? 'Basic ($2.99)' : 'Free'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Joined</p>
                  <p className="font-medium">
                    {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setEditingUser({ ...selectedUser });
                    setEditUserDialog(true);
                  }}
                >
                  <Edit2 className="w-4 h-4 mr-2" /> Edit User
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setEditingUser({ ...selectedUser });
                    setResetPasswordDialog(true);
                  }}
                >
                  <Key className="w-4 h-4 mr-2" /> Reset Password
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleExportUserData(selectedUser.id)}
                  disabled={exporting}
                >
                  <Download className="w-4 h-4 mr-2" /> Export Data
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => {
                    setUserToDelete(selectedUser);
                    setDeleteUserDialog(true);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </div>
              
              {/* Children */}
              {selectedUser.children && selectedUser.children.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> Children ({selectedUser.children.length})
                  </h3>
                  <div className="space-y-3">
                    {selectedUser.children.map((child) => (
                      <Card key={child.id}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{child.name}</p>
                              <p className="text-sm text-slate-500">
                                {child.level === 'seeds' ? '🌱 Seeds (3-5)' :
                                 child.level === 'roots' ? '🌿 Roots (6-9)' : '⚔️ Warriors (10-13)'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm">
                                <span className="text-slate-500">Scriptures:</span>{' '}
                                <span className="font-medium">{child.scriptures?.length || 0}</span>
                              </p>
                              <p className="text-sm">
                                <span className="text-slate-500">Lessons:</span>{' '}
                                <span className="font-medium">{child.lesson_progress?.filter(l => l.lesson_completed)?.length || 0}</span>
                              </p>
                            </div>
                          </div>
                          {child.badges && child.badges.length > 0 && (
                            <div className="mt-2 flex gap-1">
                              {child.badges.map((badge, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {badge.badge_type}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={editingUser.name || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="mt-1"
                  data-testid="edit-christian-user-name"
                />
              </div>
              <div>
                <Label>Subscription Plan</Label>
                <Select 
                  value={editingUser.subscription_plan || 'christian_free'} 
                  onValueChange={(v) => setEditingUser({ ...editingUser, subscription_plan: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-christian-user-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="christian_free">Free</SelectItem>
                    <SelectItem value="christian">Basic ($2.99/mo)</SelectItem>
                    <SelectItem value="christian_plus">Plus ($4.99/mo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Christian Education</Label>
                  <p className="text-xs text-slate-500">Access to Christian module</p>
                </div>
                <Switch
                  checked={editingUser.christian_education_enabled !== false}
                  onCheckedChange={(checked) => setEditingUser({ ...editingUser, christian_education_enabled: checked })}
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Academic Access</Label>
                  <p className="text-xs text-slate-500">Access to Reading, Math, etc.</p>
                </div>
                <Switch
                  checked={editingUser.academic_access === true}
                  onCheckedChange={(checked) => setEditingUser({ ...editingUser, academic_access: checked })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateUser} disabled={saving} data-testid="save-christian-user">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog} onOpenChange={setResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Set a new password for <strong>{editingUser?.name}</strong>
            </p>
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                className="mt-1"
                data-testid="christian-new-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setResetPasswordDialog(false);
              setNewPassword('');
            }}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={saving} data-testid="confirm-christian-password-reset">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteUserDialog} onOpenChange={setDeleteUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete User</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete <strong>{userToDelete?.name}</strong>?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              This will permanently delete the user account and all associated data including:
            </p>
            <ul className="text-sm text-slate-500 mt-2 list-disc list-inside">
              <li>All children profiles</li>
              <li>Lesson progress and scripture memory</li>
              <li>Badges and achievements</li>
              <li>Push notification subscriptions</li>
            </ul>
            <p className="text-sm text-red-500 mt-3 font-medium">
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeleteUserDialog(false);
              setUserToDelete(null);
            }}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser} 
              disabled={saving}
              data-testid="confirm-delete-christian-user"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade User Dialog */}
      <Dialog open={upgradeDialog.open} onOpenChange={(open) => !open && setUpgradeDialog({ open: false, user: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpCircle className="w-5 h-5 text-indigo-600" />
              Upgrade User Plan
            </DialogTitle>
            <DialogDescription>
              Upgrade {upgradeDialog.user?.name || upgradeDialog.user?.email}'s subscription plan
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-600">Current Plan:</p>
              <Badge variant="outline" className="mt-1">
                {upgradeDialog.user?.subscription_plan === 'christian_plus' ? 'Christian Plus ($4.99/mo)' :
                 upgradeDialog.user?.subscription_plan === 'christian' ? 'Christian Basic ($2.99/mo)' : 'Free'}
              </Badge>
            </div>
            
            <div>
              <Label>New Plan</Label>
              <Select value={upgradePlan} onValueChange={setUpgradePlan}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="christian">Christian Basic ($2.99/mo)</SelectItem>
                  <SelectItem value="christian_plus">Christian Plus ($4.99/mo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-indigo-50 rounded-lg p-3 text-sm text-indigo-700">
              <p className="font-medium">What this does:</p>
              <ul className="list-disc list-inside mt-1 text-indigo-600 text-xs space-y-1">
                <li>Updates user's subscription plan immediately</li>
                <li>Grants access to all plan features</li>
                <li>Does not charge the user (admin override)</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeDialog({ open: false, user: null })}>Cancel</Button>
            <Button 
              onClick={handleUpgradeUser} 
              disabled={upgrading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {upgrading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Upgrade Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Platform Users Dialog */}
      <Dialog open={addUserDialog} onOpenChange={(open) => {
        setAddUserDialog(open);
        if (!open) {
          setSearchPlatformUsers('');
          setPlatformUsers([]);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-indigo-600" />
              Add Platform Users to Christian Module
            </DialogTitle>
            <DialogDescription>
              Enable Christian Education for existing platform users
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col space-y-4 py-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search users by name or email..."
                value={searchPlatformUsers}
                onChange={(e) => {
                  setSearchPlatformUsers(e.target.value);
                  fetchPlatformUsers(e.target.value);
                }}
                className="pl-10"
              />
            </div>
            
            <div className="flex-1 overflow-auto border rounded-lg">
              {loadingPlatformUsers ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                </div>
              ) : platformUsers.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="font-medium">No users found</p>
                  <p className="text-sm">All platform users already have Christian Education enabled, or no matches found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Current Plan</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {platformUsers.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{user.name || 'No name'}</p>
                            <p className="text-sm text-slate-500">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{user.subscription_plan || 'Free'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            onClick={() => handleEnableChristianForUser(user.id, user.name || user.email)}
                            className="bg-indigo-600 hover:bg-indigo-700"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Enable
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddUserDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChristianAdminSection;
